import React, { Suspense } from 'react';
import { Route, Routes, Outlet } from 'react-router-dom';
import EmailClickTracker, { EmailTrackingLayout } from '../../components/EmailClickTracker';
import ComingSoon from '../../pages/ComingSoon';
import CrossDeviceLoginPage from '../../pages/CrossDeviceLoginPage';
import Impersonate from '../../pages/Impersonate';
import PasswordChangeSent from '../../pages/PasswordChangeSent';
import Forward from '../Forward';
import {
  CreateUserWrapper,
  HandleForwardURL,
  HandleRegisterFromShareURL,
  Logout,
  ResetPasswordCodeHandler,
} from './authRouteHandlers';
import LoadingSpinner from './LoadingSpinner';
import LookupOpportunity from './LookupOpportunity';
import {
  AcceptInvitationPage,
  AdviceViewPage,
  ArticleView,
  ConfirmEmailPage,
  EntityAssociationPage,
  EntityAssociationWizard,
  FAQ,
  HomePage,
  NewFunds,
  NewFundsAdvisor,
  Portfolio,
  PropertiesApplication,
  PropertiesApplicationAdvisor,
  PropertiesComingSoon,
  PropertiesComingSoonAdvisor,
  PropertiesEOI,
  PropertiesEOIAdvisor,
  ReferralInvitationPage,
  RejectInvitationModal,
  SettingsPage,
  ShareOpportunityPage,
} from './nonAdminLazyImports';
import {
  AdvisorAndFundOpportunity,
  AdvisorAndFundOpportunityUpdate,
  AdvisorInvestorRouteWrapper,
  ForwardRouteWrapper,
  InvestorRouteWrapper,
  HomePageWrapper,
  LoginAndFundOpportunity,
  LoginAndFundOpportunityUpdate,
} from './nonAdminRouteWrappers';

export default function NonAdminAppRoutes() {
  return (
    <>
      <EmailClickTracker />
      <Routes>
        <Route element={<EmailTrackingLayout isAdmin={false} />}>
          <Route path="/login" element={<HandleForwardURL Component="Login" />} />
          <Route path="/cross-device-login" element={<CrossDeviceLoginPage />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/signup/:invitationToken" element={<CreateUserWrapper />} />
          <Route path="/registerFromShare" element={<HandleRegisterFromShareURL />} />
          <Route
            path="/forgotPassword/:email?"
            element={<HandleForwardURL Component="ForgotPasswordParamHandler" />}
          />
          <Route path="/resetPassword" element={<ResetPasswordCodeHandler />} />
          <Route path="/password-change-sent" element={<PasswordChangeSent />} />
          <Route path="/impersonate" element={<Impersonate />} />

          <Route
            path="/advisor/invitation/:invitationId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AcceptInvitationPage />
              </Suspense>
            }
          />
          <Route
            path="/accept-invitation/:invitationId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <AcceptInvitationPage />
              </Suspense>
            }
          />
          <Route
            path="/reject-invitation/:invitationId"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <RejectInvitationModal />
              </Suspense>
            }
          />
          <Route
            path="/confirmEmail/:code"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ConfirmEmailPage />
              </Suspense>
            }
          />
          <Route
            path="/referral/invitation/:code"
            element={
              <Suspense fallback={<LoadingSpinner />}>
                <ReferralInvitationPage />
              </Suspense>
            }
          />

          <Route element={<Outlet />}>
            <Route element={<ForwardRouteWrapper />}>
              <Route path="/forward" element={<Forward />} />
            </Route>
            <Route element={<InvestorRouteWrapper />}>
              <Route
                path="/opportunities/:id/print"
                element={<LookupOpportunity type="FundOpportunity" print={true} />}
              />
              <Route
                path="/fundOpportunity/:id/fundUpdate"
                element={<LoginAndFundOpportunityUpdate />}
              />
              <Route path="/fundOpportunity/:id" element={<LoginAndFundOpportunity />} />
              <Route
                path="/opportunities/"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <NewFunds />
                  </Suspense>
                }
              />
              <Route
                path="/portfolio/opportunities/:id"
                element={<LoginAndFundOpportunityUpdate />}
              />
              <Route
                path="/portfolio"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Portfolio />
                  </Suspense>
                }
              />
              <Route
                path="/associate-entity/:investorId/:selectedEntityId/:currentState"
                element={<EntityAssociationWizard />}
              />
              <Route
                path="/associate-entity/:investorId/:currentState"
                element={<EntityAssociationWizard />}
              />
              <Route path="/associate-entity/:investorId" element={<EntityAssociationWizard />} />
              <Route path="/entity-association" element={<EntityAssociationPage />} />
              <Route
                path="/settings"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <SettingsPage />
                  </Suspense>
                }
              />
              <Route
                path="/advice-view"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <AdviceViewPage />
                  </Suspense>
                }
              />
              <Route path="/articles/:id" element={<ArticleView />} />
              <Route path="/opportunities/:id" element={<LoginAndFundOpportunity />} />
              <Route
                path="/opportunities/:id/share"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <ShareOpportunityPage />
                  </Suspense>
                }
              />
              <Route
                path="/opportunities/comingSoon/:id/fundUpdate"
                element={<LoginAndFundOpportunityUpdate />}
              />
              <Route path="/opportunities/comingSoon/:id" element={<LoginAndFundOpportunity />} />
              <Route
                path="/opportunities/comingSoon"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PropertiesComingSoon wrapperClassName="dashboard-container" />
                  </Suspense>
                }
              />
              <Route
                path="/opportunities/eoi/:id/fundUpdate"
                element={<LoginAndFundOpportunityUpdate />}
              />
              <Route path="/opportunities/eoi/:id" element={<LoginAndFundOpportunity />} />
              <Route
                path="/opportunities/eoi"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PropertiesEOI wrapperClassName="dashboard-container" />
                  </Suspense>
                }
              />
              <Route
                path="/opportunities/application/:id/fundUpdate"
                element={<LoginAndFundOpportunityUpdate />}
              />
              <Route path="/opportunities/application/:id" element={<LoginAndFundOpportunity />} />
              <Route
                path="/opportunities/application"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PropertiesApplication wrapperClassName="dashboard-container" />
                  </Suspense>
                }
              />
            </Route>

            <Route element={<AdvisorInvestorRouteWrapper />}>
              <Route
                path="/advisor"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <HomePage advisorMode={true} />
                  </Suspense>
                }
              />
              <Route
                path="/advisor/portfolio"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <Portfolio advisorMode={true} />
                  </Suspense>
                }
              />
              <Route
                path="/advisor/portfolio/opportunities/:id"
                element={<AdvisorAndFundOpportunityUpdate />}
              />
              <Route
                path="/advisor/opportunities/"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <NewFundsAdvisor />
                  </Suspense>
                }
              />
              <Route path="/advisor/opportunities/:id" element={<AdvisorAndFundOpportunity />} />
              <Route
                path="/advisor/opportunities/comingSoon/:id/fundUpdate"
                element={<AdvisorAndFundOpportunityUpdate />}
              />
              <Route
                path="/advisor/opportunities/comingSoon/:id"
                element={<AdvisorAndFundOpportunity />}
              />
              <Route
                path="/advisor/opportunities/comingSoon"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PropertiesComingSoonAdvisor wrapperClassName="dashboard-container" />
                  </Suspense>
                }
              />
              <Route
                path="/advisor/opportunities/eoi/:id/fundUpdate"
                element={<AdvisorAndFundOpportunityUpdate />}
              />
              <Route
                path="/advisor/opportunities/eoi/:id"
                element={<AdvisorAndFundOpportunity />}
              />
              <Route
                path="/advisor/opportunities/eoi"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PropertiesEOIAdvisor wrapperClassName="dashboard-container" />
                  </Suspense>
                }
              />
              <Route
                path="/advisor/opportunities/application/:id/fundUpdate"
                element={<AdvisorAndFundOpportunityUpdate />}
              />
              <Route
                path="/advisor/opportunities/application/:id"
                element={<AdvisorAndFundOpportunity />}
              />
              <Route
                path="/advisor/opportunities/application"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <PropertiesApplicationAdvisor wrapperClassName="dashboard-container" />
                  </Suspense>
                }
              />
            </Route>
            <Route path="/contactUs" element={<ComingSoon />} />

            <Route element={<InvestorRouteWrapper />}>
              <Route
                path="/faq"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <FAQ />
                  </Suspense>
                }
              />
              <Route
                path="/"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <HomePageWrapper />
                  </Suspense>
                }
              />
            </Route>
          </Route>
        </Route>
      </Routes>
    </>
  );
}
